'use client'

import {
  Checkbox,
  Divider,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
} from '@mui/material'
import { FilterDropdown } from '../FilterDropdown'
import {
  getOptionState,
  getSummaryLabels,
  toggleOption,
  type FilterMultiSelectOption,
  type FilterMultiSelectSection,
} from './helpers'

export type { FilterMultiSelectOption, FilterMultiSelectSection }

// How many summary labels the chip spells out before falling back to
// "N selected".
const MAX_SUMMARY_LABELS = 2

// A multi-select filter dropdown with checkbox options, optional titled
// sections, and group options that select several values at once (rendering
// an indeterminate checkbox while only some of their values are selected).
// The panel stays open across clicks — multi-selection is exploratory.
export function FilterMultiSelect({
  label,
  summaryPrefix,
  sections,
  value,
  onChange,
}: {
  label: string
  // Chip text when active is `${summaryPrefix}: ${summary}`.
  summaryPrefix: string
  sections: FilterMultiSelectSection[]
  value: string[]
  onChange: (value: string[]) => void
}) {
  const labels = getSummaryLabels(sections, value)
  const summary =
    value.length === 0
      ? null
      : `${summaryPrefix}: ${
          labels.length > MAX_SUMMARY_LABELS
            ? `${String(labels.length)} selected`
            : labels.join(', ')
        }`

  return (
    <FilterDropdown
      label={label}
      summary={summary}
      onClear={() => { onChange([]); }}
    >
      {/* Grow with the viewport (long lists like the 16-row grades panel can
          show fully on tall screens) but never below the old 360px cap, so
          short screens are no worse off. The 240px headroom covers the page
          header plus the trigger row above the popover. */}
      <MenuList
        sx={{
          maxHeight: 'max(360px, calc(100vh - 240px))',
          overflowY: 'auto',
          py: 0,
        }}
      >
        {/* Flat array, no Fragments: MenuList walks its children to manage
            keyboard focus, and a Fragment child breaks that (and triggers MUI
            console warnings). */}
        {sections.flatMap((section, index) => {
          const sectionKey = section.title ?? String(index)
          return [
            ...(index > 0
              ? [<Divider key={`${sectionKey}-divider`} sx={{ my: 0.5 }} />]
              : []),
            ...(section.title
              ? [
                  <ListSubheader
                    key={`${sectionKey}-title`}
                    disableSticky
                    sx={{
                      py: 0.5,
                      lineHeight: 1.5,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.disabled',
                    }}
                  >
                    {section.title}
                  </ListSubheader>,
                ]
              : []),
            ...section.options.map((option) => {
              const state = getOptionState(option, value)
              return (
                <MenuItem
                  key={`${sectionKey}-${option.label}`}
                  dense
                  sx={{ py: 0.25, minHeight: 0 }}
                  onClick={() => { onChange(toggleOption(option, value)); }}
                >
                  <Checkbox
                    size="small"
                    disableRipple
                    edge="start"
                    sx={{ py: 0.5 }}
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                    tabIndex={-1}
                  />
                  <ListItemText
                    primary={option.label}
                    slotProps={{
                      primary: {
                        variant: 'body2'
                      },
                    }}
                  />
                </MenuItem>
              )
            }),
          ]
        })}
      </MenuList>
    </FilterDropdown>
  )
}
